import { Check, ChevronRight, Loader2, X } from 'lucide-react'
import { type ReactNode, useState } from 'react'

import { cn } from '@/utils'
import type { ToolCallSummary } from './assistantBubble/renderItems'
import { humanizeToolName } from './ToolCallBlock'

type ActivityCall = Pick<
  ToolCallSummary,
  'name' | 'status' | 'errorCode' | 'superseded'
>

type ActivityTone = 'running' | 'done' | 'stopped' | 'failed'

function isRealFailure(call: ActivityCall): boolean {
  return (
    call.status === 'error' && call.errorCode !== 'aborted' && !call.superseded
  )
}

function groupLabel(calls: ActivityCall[], manyStepsLabel: string): string {
  return calls.length === 1 ? humanizeToolName(calls[0].name) : manyStepsLabel
}

export function describeToolActivity(calls: ActivityCall[]): {
  label: string
  tone: ActivityTone
} {
  const current =
    calls.find((call) => call.status === 'running') ??
    calls.find((call) => call.status === 'pending')
  if (current) {
    const step = calls.indexOf(current) + 1
    const name = humanizeToolName(current.name)
    return {
      label: groupLabel(
        calls,
        `Running step ${step} of ${calls.length}: ${name}`,
      ),
      tone: 'running',
    }
  }
  const failures = calls.filter(isRealFailure).length
  if (failures > 0) {
    return {
      label: groupLabel(calls, `${failures} of ${calls.length} steps failed`),
      tone: 'failed',
    }
  }
  if (calls.some((call) => call.errorCode === 'aborted')) {
    return {
      label: groupLabel(calls, `Stopped after ${calls.length} steps`),
      tone: 'stopped',
    }
  }
  return { label: groupLabel(calls, `Ran ${calls.length} steps`), tone: 'done' }
}

// Survives the swap from the streaming draft to the saved message, which remounts the group.
const openGroupKeys = new Set<string>()

interface ToolActivityProps {
  groupKey: string
  calls: ToolCallSummary[]
  children: ReactNode
}

export default function ToolActivity({
  groupKey,
  calls,
  children,
}: ToolActivityProps) {
  const [expanded, setExpanded] = useState(() => openGroupKeys.has(groupKey))

  function toggle() {
    if (expanded) {
      openGroupKeys.delete(groupKey)
    } else {
      openGroupKeys.add(groupKey)
    }
    setExpanded(!expanded)
  }

  const { label, tone } = describeToolActivity(calls)

  return (
    <div className="flex w-full min-w-0 flex-col items-start gap-1">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        className={cn(
          'group inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors',
          tone === 'failed'
            ? 'border-red-500/40 bg-red-500/[0.08] text-red-700 hover:bg-red-500/[0.12] dark:text-red-400'
            : 'border-transparent text-muted-foreground hover:text-foreground',
        )}
      >
        {tone === 'running' && (
          <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
        )}
        {tone === 'done' && (
          <Check className="h-3 w-3 shrink-0 text-emerald-600" />
        )}
        {tone === 'failed' && <X className="h-3 w-3 shrink-0" />}
        <span className="truncate">{label}</span>
        <ChevronRight
          className={cn(
            'h-3 w-3 shrink-0 opacity-50 transition-transform group-hover:opacity-80',
            expanded && 'rotate-90',
          )}
          aria-hidden="true"
        />
      </button>
      {expanded && children}
    </div>
  )
}
